/**
 * G-27 kit: the hook behind every microphone button.
 *
 * THE ONLY PLACE THAT KNOWS WHICH ENGINE IS IN USE. Screens ask for `supported`,
 * `status` and `interim`; none of them learns that the answer comes from Vosk
 * through the shell bridge. That indirection is the point — Android's own
 * recogniser was the cheap experiment G-27 asked for first, and if it is ever
 * worth retrying, or if a language moves elsewhere, this file changes and the
 * consuming screens do not.
 *
 * RELEASING THE MICROPHONE IS NOT OPTIONAL, and it is stricter here than in
 * `BarcodeScanner.tsx`, whose cleanup discipline this copies. A leaked camera
 * wastes battery; a leaked recogniser shows a live microphone indicator on a
 * shared tablet for the rest of the shift, and holds a few hundred megabytes of
 * model while it does.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  cancelDictation,
  dictationErrorMessage,
  dictationSupported,
  onDictationError,
  onDictationPartial,
  onDictationResult,
  onDictationStopped,
  startDictation,
  stopDictation,
  type DictationListener,
  type DictationLocale,
} from "../../platform/dictation";

/**
 * `starting` is separate from `listening` on purpose. The first use of a
 * language unpacks its model out of the APK — Ukrainian is 416 MB — and
 * `/code-review` 2026-09-12 caught the button claiming to listen throughout,
 * so a florist would speak into a recogniser that did not exist yet and lose
 * every word while being told they were heard.
 */
export type DictationStatus =
  | "idle"
  | "starting"
  | "listening"
  | "denied"
  | "error";

export interface UseDictationOptions {
  /** Called with each FINAL transcript. Interim words never reach it. */
  onText: (text: string) => void;
}

export interface UseDictation {
  /** False in any plain browser. When false, render no microphone at all. */
  supported: boolean;
  status: DictationStatus;
  /** Words heard so far in this utterance, shown but never committed. */
  interim: string;
  /** A florist-facing sentence, or null. */
  error: string | null;
  start: (locale: DictationLocale) => Promise<void>;
  stop: () => Promise<void>;
}

export function useDictation({ onText }: UseDictationOptions): UseDictation {
  // Evaluated once: the bridge cannot appear or vanish mid-session, and calling
  // it during render on every keystroke would be wasteful.
  const [supported] = useState(() => dictationSupported());
  const [status, setStatus] = useState<DictationStatus>("idle");
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);

  /**
   * The parent's callback, pinned.
   *
   * Without this the listener effect would depend on a function the parent
   * re-creates every render, so the microphone would be torn down and
   * re-registered on every keystroke — `BarcodeScanner.tsx:114-116` learned this
   * about the camera.
   */
  const onTextRef = useRef(onText);
  useEffect(() => {
    onTextRef.current = onText;
  }, [onText]);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    const handles: DictationListener[] = [];

    /**
     * Registering a listener is itself async. If the screen closes during that
     * await, cleanup has already run and found nothing to remove — so the handle
     * is removed here, at the point it arrives. Same shape as the stream that
     * lands after an unmount in `BarcodeScanner.tsx:150-153`.
     */
    const keep = (handle: DictationListener | undefined) => {
      if (!handle) return;
      if (cancelled) {
        void handle.remove();
        return;
      }
      handles.push(handle);
    };

    void onDictationPartial((text) => {
      if (!cancelled) setInterim(text);
    }).then(keep);

    void onDictationResult((text) => {
      if (cancelled) return;
      setInterim("");
      onTextRef.current(text);
    }).then(keep);

    void onDictationError((err) => {
      if (cancelled) return;
      setInterim("");
      setStatus(err.code === "PERMISSION_DENIED" ? "denied" : "error");
      setError(dictationErrorMessage(err));
    }).then(keep);

    // Vosk gave up on its own. Without this the button keeps its live fill over
    // a microphone that is already closed.
    void onDictationStopped(() => {
      if (cancelled) return;
      setInterim("");
      setStatus("idle");
    }).then(keep);

    return () => {
      cancelled = true;
      // Belt and braces, deliberately: cancel the session AND drop the
      // listeners. Either alone leaves something behind — an open microphone,
      // or a callback writing into a screen that is gone.
      void cancelDictation();
      for (const handle of handles) void handle.remove();
    };
  }, [supported]);

  const start = useCallback(
    async (locale: DictationLocale) => {
      if (!supported) return;
      setError(null);
      setInterim("");
      // `starting` until the native side reports the microphone is actually
      // open — which on a first use of a language means waiting out a model
      // unpack. Claiming to listen before then loses whatever is said into it.
      setStatus("starting");
      try {
        await startDictation(locale);
        setStatus("listening");
      } catch (err: unknown) {
        setStatus(
          (err as { code?: string } | null)?.code === "PERMISSION_DENIED"
            ? "denied"
            : "error",
        );
        setError(dictationErrorMessage(err));
      }
    },
    [supported],
  );

  const stop = useCallback(async () => {
    if (!supported) return;
    setStatus("idle");
    setInterim("");
    try {
      await stopDictation();
    } catch {
      // Stopping something that already stopped is not a failure worth a
      // sentence in front of a florist.
    }
  }, [supported]);

  return { supported, status, interim, error, start, stop };
}
